import { Component, OnInit, OnDestroy, ViewChild, ElementRef,AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NavigationExtras, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { jwtDecode } from 'jwt-decode';
import { LoggingService } from '../../services/logging.service';
import { Subscription } from 'rxjs';
import { HrDashboardService } from '../../services/hr-dashboard.service';
import { TenantRouterService } from '../../services/tenant-router.service';

interface Payload {
  email: string;
  sub: number;
  personType: string;
  managerId : number;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm !: FormGroup;
  errorMessage: string = '';
  personType : string = ''; 
  isSubmitting: boolean = false; 
  private loginSubscription !: Subscription;
  showPassword = false;
  forgotPasswordEmail: string = '';
  isForgotSubmitting: boolean = false;
  showForgotPasswordForm: boolean = false;
  successMessage: string = ''; 

  @ViewChild('passwordInput') passwordInput!: ElementRef;
  @ViewChild('emailInput') emailInput!: ElementRef;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
        private hrDashboardService: HrDashboardService,
    private loggingService : LoggingService, private tenantRouter: TenantRouterService
   ) { }

  ngOnInit(): void {
    this.authService.logout();
    this.tenantRouter.logoutRedirect();
    this.buildLoginForm();    
  }

  ngAfterViewInit(): void {
    // Focus email when page loads
    if (this.emailInput) {
      this.emailInput.nativeElement.focus();
      this.loggingService.logDebug('Auto-focused on email field at page load.');
    }
  }

  ngOnDestroy() {
    this.loginSubscription.unsubscribe();
  }

  buildLoginForm() {
    // Set up the form with username, password
    this.loggingService.logInfo("Creating the form with validator in user login");
    this.loginForm = this.fb.group({
      Email: ['', [Validators.required,Validators.minLength(3) ]],
      Password: ['',[ Validators.required,]]
    });
  }

  payload !: Payload;

 


    focusPassword(): void {
    if (this.passwordInput) {
      this.passwordInput.nativeElement.focus();
      this.loggingService.logDebug('Auto-focused on password field after entering email.');
    }
  }


  onSubmit(): void {
    if (this.isSubmitting) return; // prevent multiple clicks
    this.isSubmitting = true;

    this.loggingService.logDebug("Submitting the form for user login");
    if (this.loginForm.invalid) {
      this.isSubmitting = false;

     const emailErrors = this.loginForm.get('Email')?.errors;
    const passwordErrors = this.loginForm.get('Password')?.errors;

    if (emailErrors) {
      if (emailErrors['required']) {
        this.errorMessage = 'Email  is required';
      }
        else {
        this.errorMessage = 'Invalid username.';
      }
    } else if (passwordErrors) {
      if (passwordErrors['required']) {
        this.errorMessage = 'Password is required';
      } 
    }
    this.loggingService.logError(this.errorMessage);
      return;
    }

    

    const password = this.loginForm.get('Password')?.value;
   
    const loginData = this.loginForm.value;

    try {
      this.loginSubscription = this.authService.login(loginData).subscribe({
        next: (response) => {
          this.isSubmitting = false;
          this.loggingService.logDebug("Access token "+response.access_token);
          // Assume response returns an object { access_token: 'abc123' }
          const token = response.access_token;
          if (token) {            
            this.payload = this.getDecodedAccessToken(token);
            if(this.payload !== null) {
              sessionStorage.setItem('personSessionToken', token);
              sessionStorage.setItem('user_role', this.payload.personType);  
              sessionStorage.setItem('user_id', this.payload.sub.toString()); 
              if(this.payload.managerId) {
                sessionStorage.setItem('manager_id', this.payload.managerId.toString());  
              }          
              if (this.payload.personType === 'manager') {
                this.loggingService.logDebug("Redirecting to manager dashboard after successful response");
                this.tenantRouter.navigate('manager-dashboard');
              } else if(this.payload.personType === 'user') {
                this.loggingService.logDebug("Redirecting to user dashboard after successful response");
                this.tenantRouter.navigate('user-dashboard');
              } else if(this.payload.personType === 'hr') {
                this.loggingService.logDebug("Redirecting to hr dashboard after successful response");
          this.hrDashboardService.getAllHrDetails(this.payload.sub, 'hr').subscribe({
  next: (hrDetails: any) => {
    const tenantId = hrDetails?.hr?.tenantId;
    if (tenantId) {
      sessionStorage.setItem('tenantId', tenantId.toString());
      this.loggingService.logDebug('tenantId set in sessionStorage: ' + tenantId);
    } else {
      this.loggingService.logError('HR details returned no tenantId');
    }
    //  Navigate after tenantId is set
    this.tenantRouter.navigate('hr-dashboard');
  },
  error: (err: any) => {
    this.loggingService.logError('Failed to fetch HR details: ' + err.message);
    this.errorMessage = 'Login succeeded but failed to load HR details';
    this.tenantRouter.navigate('hr-dashboard');
  },
  complete: () => {
    this.isSubmitting = false;
  }
});


              } else {
                this.loggingService.logError('Invalid Person Type');
                this.tenantRouter.navigate('login');
              }
            } else {
              this.loggingService.logError("Payload is null");
              this.errorMessage = 'Login failed: session token error';
            }                
          } else {
            this.loggingService.logError("Login failed: session token missing.");
            this.errorMessage = 'Login failed: session token missing.';
          }
        },
        error: (error) => {
          this.isSubmitting = false;
          const serverMessage =
          error?.error?.message ||
          error?.message ||
          (typeof error?.error === 'string' ? error.error : null) ||
          'Login failed. Please try again.';
        this.loggingService.logError(serverMessage);

        this.errorMessage = "Email or Password is Invalid";

        this.errorMessage = serverMessage;

          this.loginForm.patchValue({ Password: '' });

        }
      });        
    } catch(error : any) {
      this.isSubmitting = false;
      this.loggingService.logError(error.error.message || 'Login failed. Please try again.');
      this.errorMessage = error.message || 'Invalid credentials';

      this.loginForm.patchValue({ Password: '' });
    }   
    
  }

  getDecodedAccessToken(token : string) : any {
    try {
      this.loggingService.logDebug("Decoding the token to get personType");
      return jwtDecode<Payload>(token);
    } catch(error) {
      return null;
    }
  }

  
togglePassword() {
  this.showPassword = !this.showPassword;
}

 toggleForgotPassword(): void {
    this.showForgotPasswordForm = !this.showForgotPasswordForm;
    this.errorMessage = '';
    this.successMessage = '';
    this.forgotPasswordEmail = '';
  }

  onForgotPassword(): void {
    if (!this.forgotPasswordEmail) {
      this.errorMessage = 'Please enter your email to reset password.';
      this.successMessage = '';
      this.loggingService.logError(this.errorMessage);
      return;
    }

    this.isForgotSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.loggingService.logInfo('Submitting forgot password request for ' + this.forgotPasswordEmail);

    this.authService.forgotPassword(this.forgotPasswordEmail).subscribe({
      next: (res: any) => {
        this.isForgotSubmitting = false;
        this.errorMessage = '';
           this.successMessage = 'Password sent successfully. Please check your email.';
        this.loggingService.logInfo('Forgot password link sent successfully');
        console.log('Forgot Password Response:', res);
      },
      error: (err) => {
        this.isForgotSubmitting = false;
        this.successMessage = '';
        this.errorMessage = err.error?.message || err.message || 'Failed to send reset link.';
        this.loggingService.logError('Forgot password failed', err);
        console.error('Forgot Password Error:', err);
      }
    });
  }

}



